/**
 * Every dictionary this application has, assembled in one place.
 *
 * ADDING A LANGUAGE IS A DATA CHANGE. Create `dict/<tag>/`, copy the namespaces you want to cover,
 * add one entry to LANGUAGE_NAMES in ../languages.js, and it appears in every picker. No refactor,
 * no registration list to remember.
 *
 * THE OFFERED SET IS DERIVED FROM WHAT EXISTS, NEVER DECLARED. `LANGUAGES = Object.keys(DICTS)`.
 * This is the repo's own hard-won rule, learned the expensive way in forms: a `languages` list stored
 * beside the strings went stale when a serializer forgot it, and a tenant's Spanish became
 * unreachable while it was still sitting right there in the document (see `bundleLanguages` in
 * modules/forms/i18n.js and `languages` in forms/backend/app/services/i18n.py, which both say so at
 * length). A derived set cannot go stale: adding a language IS adding the folder.
 *
 * A NAMESPACE PER SCREEN-AREA, not one flat file. Two reasons: a translator works one area at a time
 * and wants to see it whole, and `useT('auth')` at a call site is a much smaller thing to get wrong
 * than a global id space where `title` means eleven different things.
 *
 * COVERAGE IS PARTIAL BY DESIGN. A namespace that exists only in `en` is normal — every lookup falls
 * back string-by-string (see ../t.js), so a screen translated tomorrow is a file added, not a
 * migration.
 */
import enAuth from './en/auth'
import enChrome from './en/chrome'
import enCommon from './en/common'
import enDailyReport from './en/dailyReport'
import enForms from './en/forms'
import enModules from './en/modules'
import enPortal from './en/portal'
import enSettings from './en/settings'
import enTimekeeping from './en/timekeeping'
import enTranslate from './en/translate'

import esAuth from './es/auth'
import esChrome from './es/chrome'
import esCommon from './es/common'
import esDailyReport from './es/dailyReport'
import esForms from './es/forms'
import esModules from './es/modules'
import esPortal from './es/portal'
import esSettings from './es/settings'
import esTimekeeping from './es/timekeeping'
import esTranslate from './es/translate'

export const DICTS = {
  en: {
    common: enCommon,
    auth: enAuth,
    chrome: enChrome,
    modules: enModules,
    portal: enPortal,
    timekeeping: enTimekeeping,
    dailyReport: enDailyReport,
    forms: enForms,
    settings: enSettings,
    translate: enTranslate,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    chrome: esChrome,
    modules: esModules,
    portal: esPortal,
    timekeeping: esTimekeeping,
    dailyReport: esDailyReport,
    forms: esForms,
    settings: esSettings,
    translate: esTranslate,
  },
}

/** Derived from the dictionaries that EXIST. Never declared. See above. */
export const LANGUAGES = Object.keys(DICTS)

/** One namespace across every language: `{en: {...}, es: {...}}`, the shape `makeT` reads. */
export function namespaceDict(ns) {
  const out = {}
  LANGUAGES.forEach((lang) => { out[lang] = (DICTS[lang] || {})[ns] || {} })
  return out
}
